import React from 'react';
import { Box, Typography } from '@mui/material';
import { BACKPACK_CONTENT } from '../content/backpackContent';
import { BRAVE_CONTENT } from '../content/braveContent';
import { NIGHTLY_CONTENT } from '../content/nightlyContent';
import { OVERVIEW_CONTENT } from '../content/overviewContent';
import { GUIDE_PHASES, PHANTOM_CONTENT, type GuideStepContent } from '../content/phantomContent';
import { SOLFLARE_CONTENT } from '../content/solflareContent';
import { guideTokens } from '../theme/guideTokens';

function PrintWalletSection({
  title,
  lead,
  steps,
}: {
  title: string;
  lead: string;
  steps: readonly GuideStepContent[];
}) {
  return (
    <>
      <Box sx={{ borderTop: '1px solid #ddd', my: 3 }} />
      <Typography
        component="h2"
        sx={{ fontFamily: guideTokens.fontDisplay, fontWeight: 800, fontSize: '1.35rem', mb: 1 }}
      >
        {title}
      </Typography>
      <Typography sx={{ mb: 1.5, lineHeight: 1.65 }}>{lead}</Typography>
      <Typography sx={{ mb: 2, fontSize: '0.85rem', color: '#5C5D63' }}>
        Phases: {GUIDE_PHASES.map((p) => p.label).join(' → ')}
      </Typography>

      {steps.map((step, index) => (
        <Box key={step.title} sx={{ mb: 2.5, breakInside: 'avoid' }}>
          <Typography sx={{ fontWeight: 700, fontFamily: guideTokens.fontDisplay, mb: 0.75 }}>
            {index + 1}. {step.title}
          </Typography>
          {step.body.map((p) => (
            <Typography key={p.slice(0, 40)} sx={{ mb: 0.75, lineHeight: 1.6, fontSize: '0.95rem' }}>
              {p}
            </Typography>
          ))}
          {step.warnings && step.warnings.length > 0 && (
            <Box sx={{ mb: 1, pl: 1.5, borderLeft: '3px solid #D97706' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Important</Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {step.warnings.map((w) => (
                  <li key={w.slice(0, 40)} style={{ marginBottom: 4, fontSize: '0.9rem' }}>
                    {w}
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {step.tips && step.tips.length > 0 && (
            <Box sx={{ mb: 1, pl: 1.5, borderLeft: '3px solid #1E9D63' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Tips</Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {step.tips.map((t) => (
                  <li key={t.slice(0, 40)} style={{ marginBottom: 4, fontSize: '0.9rem' }}>
                    {t}
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {step.link && (
            <Typography sx={{ mb: 1, fontSize: '0.9rem' }}>
              {step.link.label}: {step.link.href}
            </Typography>
          )}
          {step.image && (
            <Box sx={{ mt: 1, mb: 0.5 }}>
              <Box
                component="img"
                src={step.image.src}
                alt={step.image.alt}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 320,
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                }}
              />
              <Typography sx={{ mt: 0.5, fontSize: '0.8rem', color: '#5C5D63' }}>
                {step.image.caption}
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </>
  );
}

/** Print → Save as PDF: Overview + all wallet walkthroughs. */
export const GuidePrintDocument: React.FC = () => {
  return (
    <Box
      id="wallet-guide-print"
      aria-hidden
      sx={{
        display: 'none',
        '@media print': {
          display: 'block',
          color: '#15161A',
          fontFamily: guideTokens.fontBody,
        },
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: guideTokens.fontDisplay,
          fontWeight: 800,
          fontSize: '1.75rem',
          mb: 0.5,
        }}
      >
        Canadawale Yar — Wallet guide
      </Typography>
      <Typography sx={{ mb: 3, color: '#5C5D63', fontSize: '0.95rem' }}>
        Connect, send & receive CWY · {OVERVIEW_CONTENT.eyebrow}
      </Typography>

      <Typography
        component="h2"
        sx={{ fontFamily: guideTokens.fontDisplay, fontWeight: 800, fontSize: '1.35rem', mb: 1 }}
      >
        {OVERVIEW_CONTENT.title}
      </Typography>
      <Typography sx={{ mb: 2, lineHeight: 1.65 }}>{OVERVIEW_CONTENT.lead}</Typography>

      <Typography sx={{ fontWeight: 700, mb: 0.75, fontFamily: guideTokens.fontDisplay }}>
        What you’ll do
      </Typography>
      <Box component="ol" sx={{ mt: 0, mb: 2.5, pl: 2.5 }}>
        {OVERVIEW_CONTENT.checklist.map((item) => (
          <li key={item} style={{ marginBottom: 4, lineHeight: 1.55 }}>
            {item}
          </li>
        ))}
      </Box>

      {OVERVIEW_CONTENT.sections.map((section) => (
        <Box key={section.heading} sx={{ mb: 2, breakInside: 'avoid' }}>
          <Typography sx={{ fontWeight: 700, fontFamily: guideTokens.fontDisplay, mb: 0.5 }}>
            {section.heading}
          </Typography>
          {section.body.map((p) => (
            <Typography key={p.slice(0, 40)} sx={{ mb: 0.75, lineHeight: 1.6, fontSize: '0.95rem' }}>
              {p}
            </Typography>
          ))}
          {'bullets' in section && section.bullets && (
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
              {section.bullets.map((b) => (
                <li key={b.slice(0, 40)} style={{ marginBottom: 4, lineHeight: 1.55 }}>
                  {b}
                </li>
              ))}
            </Box>
          )}
        </Box>
      ))}

      <Typography sx={{ mt: 1, mb: 2, fontSize: '0.9rem' }}>
        Rewards program: {OVERVIEW_CONTENT.rewardsUrl}
      </Typography>

      <PrintWalletSection
        title={PHANTOM_CONTENT.title}
        lead={PHANTOM_CONTENT.lead}
        steps={PHANTOM_CONTENT.steps}
      />
      <PrintWalletSection
        title={NIGHTLY_CONTENT.title}
        lead={NIGHTLY_CONTENT.lead}
        steps={NIGHTLY_CONTENT.steps}
      />
      <PrintWalletSection
        title={SOLFLARE_CONTENT.title}
        lead={SOLFLARE_CONTENT.lead}
        steps={SOLFLARE_CONTENT.steps}
      />
      <PrintWalletSection
        title={BACKPACK_CONTENT.title}
        lead={BACKPACK_CONTENT.lead}
        steps={BACKPACK_CONTENT.steps}
      />
      <PrintWalletSection
        title={BRAVE_CONTENT.title}
        lead={BRAVE_CONTENT.lead}
        steps={BRAVE_CONTENT.steps}
      />
    </Box>
  );
};
